import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { FancySparkles } from "design-system/atoms/icons/FancySparkles"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Bell, CheckCircle, TrendingUp } from "lucide-react"
import { ComponentProps } from "react"
import { useTranslation } from "react-i18next"

type Props = {
  buttonProps?: React.ComponentProps<typeof Button>
  trigger?: React.ReactNode
  side?: ComponentProps<typeof PopoverContent>["side"]
}

export function SignalsExplanationPopover({ buttonProps, trigger, side }: Props) {
  const { t } = useTranslation()

  return (
    <Popover>
      <PopoverTrigger asChild={!!trigger}>
        {trigger || (
          <Button variant="ghost" {...buttonProps}>
            <span>{t("dashboard.signals.explanation.whatAreSignals")}</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="max-w-[440px] flex flex-col gap-2 p-5" align="center" side={side}>
        <div className="flex flex-col gap-1.5">
          <h4 className="font-medium leading-none">{t("dashboard.signals.explanation.title")}</h4>
          <p className="text-sm text-muted-foreground">
            {t("dashboard.signals.explanation.description")}
          </p>
        </div>
        <div className="grid gap-3">
          {[
            {
              icon: <FancySparkles className="size-4.5 text-primary" strokeWidth={1} />,
              title: t("dashboard.signals.explanation.features.automaticDetection.title"),
              description: t(
                "dashboard.signals.explanation.features.automaticDetection.description",
              ),
            },
            {
              icon: <TrendingUp className="size-4.5 text-primary" />,
              title: t("dashboard.signals.explanation.features.smartAnalysis.title"),
              description: t("dashboard.signals.explanation.features.smartAnalysis.description"),
            },
            {
              icon: <CheckCircle className="size-4.5 text-primary" />,
              title: t("dashboard.signals.explanation.features.organizeSignals.title"),
              description: t("dashboard.signals.explanation.features.organizeSignals.description"),
            },
            {
              icon: <Bell className="size-4.5 text-primary" />,
              title: t("dashboard.signals.explanation.features.instantAlerts.title"),
              description: t("dashboard.signals.explanation.features.instantAlerts.description"),
              comingSoon: true,
            },
          ].map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="mt-0.5">{item.icon}</div>
              <div className="flex flex-col gap-0.5">
                <div className="flex flex-row items-center justify-start flex-1 gap-2">
                  <span className="text-base font-medium">{item.title}</span>
                  {"comingSoon" in item && item.comingSoon && (
                    <Badge variant="secondary" className="text-xs py-0 px-1">
                      {t("dashboard.signals.explanation.comingSoon")}
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{item.description}</span>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
